import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, MenuItem, Alert, CircularProgress, Grid
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import ModuleHub from '../components/ModuleHub';
import { getBills, createBill, updateBillStatus, getVendors } from '../../../services/api';

const BillsManagement = () => {
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ vendor_id: '', bill_date: new Date().toISOString().split('T')[0], due_date: '', subtotal: 0, tax_amount: 0, total_amount: 0, notes: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [billsRes, vendorsRes] = await Promise.all([getBills(), getVendors()]);
      setBills(billsRes.data || []);
      setVendors(vendorsRes.data || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      const total = parseFloat(form.subtotal) + parseFloat(form.tax_amount);
      await createBill({ ...form, vendor_id: parseInt(form.vendor_id), total_amount: total });
      setDialogOpen(false);
      loadData();
    } catch (err) { setError(err.message); }
  };

  const statusColor = { draft: 'default', pending: 'warning', approved: 'info', paid: 'success', overdue: 'error' };

  return (
    <ModuleHub moduleId="accounts-payable" showFeatures={false}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>Enter Bill</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead><TableRow><TableCell>Bill #</TableCell><TableCell>Vendor</TableCell><TableCell>Date</TableCell><TableCell>Due</TableCell><TableCell align="right">Total</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {bills.map(b => (
                <TableRow key={b.id}>
                  <TableCell><strong>{b.bill_number}</strong></TableCell>
                  <TableCell>{b.vendor?.name}</TableCell>
                  <TableCell>{b.bill_date}</TableCell>
                  <TableCell>{b.due_date}</TableCell>
                  <TableCell align="right">{parseFloat(b.total_amount).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' })}</TableCell>
                  <TableCell><Chip label={b.status} size="small" color={statusColor[b.status] || 'default'} /></TableCell>
                  <TableCell>
                    {b.status === 'draft' && <Button size="small" onClick={() => updateBillStatus(b.id, 'approved').then(loadData)}>Approve</Button>}
                    {['approved', 'partial'].includes(b.status) && <Button size="small" color="success" onClick={() => updateBillStatus(b.id, 'paid').then(loadData)}>Mark Paid</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enter Bill</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}><TextField fullWidth select label="Vendor" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}>{vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}</TextField></Grid>
            <Grid item xs={6}><TextField fullWidth type="date" label="Bill Date" InputLabelProps={{ shrink: true }} value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="date" label="Due Date" InputLabelProps={{ shrink: true }} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="number" label="Subtotal" value={form.subtotal} onChange={(e) => setForm({ ...form, subtotal: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="number" label="Tax Amount" value={form.tax_amount} onChange={(e) => setForm({ ...form, tax_amount: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleSave}>Save Bill</Button></DialogActions>
      </Dialog>
    </ModuleHub>
  );
};

export default BillsManagement;
